import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

import getFilterValues from 'api/getFilterValues';
import getSanitizedEntityFields from 'components/GlobalSearch/getSanitizedEntityFields';
import AccountContext from 'contexts/AccountContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import GqlClientContext from 'contexts/GqlClientContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import relativeDateRangeToDateRange from 'relativeDateRangeToDateRange';

import useEntityParams from './useEntityParams';
import { useBuildEntityLink } from '../../../components/GlobalSearch/hooks/useEntityLink';
import mergeFilterInputs from '../../../mergeFilterInputs';
import PopupEntityAppContext from '../../../contextProviders/SplashScreenProviders/MiscProviders/PopupEntityAppProvider/PopupEntityAppContext';
import V3EntityAppContext from '../../V3/Workspace/screens/EntityAppScreen/V3EntityAppContext';

const useQueryArgs = (
  selectedEntity: EntityDetails.Entity | undefined,
  entitySearchText: string,
) => {
  const { weekStartsOn } = useContext(AccountContext);
  const { datasets, performanceDatasets } = useContext(
    DatasetDefinitionsContext,
  );
  const [queryArgs, setQueryArgs] = useState<
    | {
        filterInput: FilterInput;
        dateScope: DateRangeInput;
        field: string;
      }
    | undefined
  >();

  useEffect(() => {
    if (!selectedEntity) {
      setQueryArgs(undefined);
      return;
    }

    // Guard against drift between the persisted entity definition and the
    // dataset: if the primary field no longer exists, don't fire an invalid
    // query. Fails open when the dataset definition can't be found.
    const datasetDefinition = [...datasets, ...performanceDatasets].find(
      (d) => d.type === selectedEntity.entityDataset,
    );
    const { isPrimaryFieldValid } = getSanitizedEntityFields({
      entity: selectedEntity,
      datasetDefinition,
    });
    if (!isPrimaryFieldValid) {
      setQueryArgs(undefined);
      return;
    }

    const dateScope = selectedEntity.activeDateScope?.relativeDateRange
      ? relativeDateRangeToDateRange({
          relativeDateRange: selectedEntity.activeDateScope.relativeDateRange,
          startOfWeek: weekStartsOn,
          dateField: selectedEntity.activeDateScope.dateField,
        })
      : {};

    setQueryArgs({
      field: selectedEntity.primaryField,
      filterInput: mergeFilterInputs(
        {
          dataType: [selectedEntity.entityDataset],
          wildcardFilters: [
            {
              field: selectedEntity.primaryField,
              pattern: `*${entitySearchText}*`,
            },
          ],
        },
        selectedEntity.isActiveFilter,
      ),
      dateScope,
    });
  }, [
    datasets,
    entitySearchText,
    performanceDatasets,
    selectedEntity,
    weekStartsOn,
  ]);

  return queryArgs;
};

const DEBOUNCE_MS = process.env.NODE_ENV === 'test' ? 200 : 1000;

export const useGetEntityValues = (
  selectedEntity: EntityDetails.Entity | undefined,
  entitySearchText: string,
) => {
  const { client } = useContext(GqlClientContext);
  const { selectedAccount } = useContext(AccountPickerContext);

  const queryArgs = useQueryArgs(selectedEntity, entitySearchText);

  const getEntityValues = useCallback((): Promise<string[]> => {
    if (!queryArgs) {
      return Promise.resolve([]);
    }
    return getFilterValues({
      field: queryArgs.field,
      filterInput: queryArgs.filterInput,
      dateScope: queryArgs.dateScope,
      client,
      accountId: selectedAccount.accountId,
    });
  }, [client, queryArgs, selectedAccount.accountId]);

  return getEntityValues;
};

export const useEntityValues = (
  selectedEntity: EntityDetails.Entity | undefined,
) => {
  const { value } = useEntityParams();
  const [entitySearchText, setEntitySearchText] = useState<string>('');
  const [values, setValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const getEntityValues = useGetEntityValues(selectedEntity, entitySearchText);

  const onEntitySearchTextChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEntitySearchText(event.target.value);
    },
    [],
  );

  // Some unusual code here to enable a debounce and handle race conditions
  useEffect(() => {
    setIsLoading(true);
    let isActive = true;

    // Timeout for debounce
    setTimeout(() => {
      if (!isActive) {
        // Bounced
        return;
      }
      getEntityValues()
        .then((newValues) => {
          if (!isActive) {
            // Handle race condition potential
            return;
          }

          if (value) {
            // Always have the selected entity visible
            setValues(_.uniq([...newValues, value]).sort());
          } else {
            setValues(newValues);
          }

          setIsLoading(false);
        })
        .catch((error) => {
          // Never leave the dropdown spinner hanging if the lookup fails.
          console.log('Entity value lookup failed', error);
          if (!isActive) {
            return;
          }
          setValues(value ? [value] : []);
          setIsLoading(false);
        });
    }, DEBOUNCE_MS); // 1s debounce

    return () => {
      isActive = false;
    };
  }, [entitySearchText, getEntityValues, value]);

  return {
    values,
    isLoading,
    entitySearchText,
    onEntitySearchTextChanged,
  };
};

const useEntityOptions = ({
  selectedEntity,
  onEntitySelected,
}: {
  selectedEntity: EntityDetails.SelectedEntity | undefined;
  onEntitySelected: (e: string) => void;
}) => {
  const v3EntityAppContext = useContext(V3EntityAppContext);
  const popupEntityAppContext = useContext(PopupEntityAppContext);
  const {
    values: entityValues,
    isLoading: isLoadingEntityValues,
    entitySearchText,
    onEntitySearchTextChanged,
  } = useEntityValues(selectedEntity ? selectedEntity.type : undefined);

  const buildEntityLink = useBuildEntityLink(
    selectedEntity ? selectedEntity.type : undefined,
  );

  const navigate = useNavigate();
  const lastEntityId = useRef<string | undefined>(undefined);
  const shouldClearOptions = useCallback(() => {
    if (lastEntityId.current === undefined || !selectedEntity) {
      return true;
    }

    return lastEntityId.current !== selectedEntity.type.id;
  }, [selectedEntity]);

  const buildOptions = useCallback(() => {
    if (!selectedEntity) {
      return [];
    }
    if (isLoadingEntityValues) {
      return [];
    }

    lastEntityId.current = selectedEntity.type.id;

    return _.uniq(entityValues).map((entity) => ({
      key: entity,
      value: entity,
      label: entity,
      isSelected: entity === selectedEntity.fieldValue,
      onSelected: () => {
        onEntitySelected(entity);
        if (popupEntityAppContext) {
          return;
        }

        if (v3EntityAppContext) {
          v3EntityAppContext.onEntitySelected(entity);
          return;
        }

        const link = buildEntityLink(entity);
        if (link) {
          navigate(link);
        } else {
          alert('Something went wrong!');
        }
      },
    }));
  }, [
    selectedEntity,
    isLoadingEntityValues,
    entityValues,
    onEntitySelected,
    popupEntityAppContext,
    v3EntityAppContext,
    buildEntityLink,
    navigate,
  ]);
  const [options, setOptions] = useState<DropdownOption[]>(buildOptions);
  useEffect(() => {
    setOptions((currentOptions) => {
      if (shouldClearOptions()) {
        return buildOptions();
      }

      const newOptions = _.sortBy(
        _.uniqBy([...buildOptions(), ...currentOptions], 'key'),
        'label',
      );

      return newOptions.map((o) => ({
        ...o,
        isSelected: selectedEntity
          ? o.value === selectedEntity.fieldValue
          : false,
      }));
    });
  }, [buildOptions, selectedEntity, shouldClearOptions]);

  return {
    entityOptions: options,
    entitySearchText,
    onEntitySearchTextChanged,
    isLoadingEntityOptions: isLoadingEntityValues,
  };
};

export default useEntityOptions;
